(ns arthur.flow.address
  "Tier 2 keys: what a dense block is NAMED, and what that name is made of.

  Until step 9 a block's key was a descriptive string — \"take/geom\",
  \"footage/iris-pos\" — and `flow/freeze` said of them: \"they become the blocks'
  sha256 when the backend arrives and nothing above here changes, which is the
  point of a handle.\" This is that, and nothing above it did change.

  A KEY IS A HASH OVER INPUTS, NOT OVER BYTES. Both are content addressing and
  they answer different questions. Hashing the bytes tells you whether two blocks
  are identical; hashing the inputs tells you, BEFORE computing anything, which
  block the current settings want — which is the question a cache is asked. It is
  also what makes a stale bake unreachable rather than wrong: change a knob and
  the scene names a key that no longer exists, so the worst case is a re-freeze.
  Nothing in the system can serve old landmarks under new settings.

  THE DETECTOR VERSION IS IN IT, and docs/architecture.md is explicit about why:
  a model upgrade that silently reuses old landmarks presents as \"the tool got
  worse\", with no event to attach it to. It enters through the ANALYSIS id, which
  every block descriptor names, so it cannot be in one block's key and missing
  from another's.

  WHICH KNOBS. `block-knobs` below is the invalidation table: for each block, the
  settings its BYTES depend on. Getting it wrong in either direction is a bug with
  a different symptom — too few and a knob silently does nothing until a reload,
  too many and every unrelated tweak throws away a good bake — so it is not
  trusted. `address-test` re-freezes the take once per knob and asserts the
  biconditional: a block's bytes changed if and only if its key changed. That is
  what keeps this table honest, because reading it will not.

  Not a namespace with state, and not a registry: every function here is
  `(f inputs) -> string`."
  (:require [arthur.domain.canon :as canon]
            [arthur.domain.sha256 :as sha]))

(def ^:const scheme
  "The addressing scheme's own version, inside every key.

  If the shape of a descriptor changes — a field added, a field's meaning
  revised — then keys computed the old way name bytes produced by code that no
  longer exists. Bumping this makes every one of them unreachable in one edit,
  which is the cheap version of a migration."
  1)

;; ---------------------------------------------------------------------------
;; the analysis artifact

(defn analysis-descriptor
  "The canonical text naming one analysis artifact: which detector, at which
  version, over which source.

  `:fps` and `:aspect` are in here rather than in the block descriptors, and that
  is not an arrangement of convenience. Both are properties of the FOOTAGE — the
  source cadence and the pixel aspect of the frames it was decoded from — and both
  reach tier 2 bytes: aspect through every landmark that is de-anisotropised
  before a fit, and fps through every dwell that is specified in seconds
  (`condition/quantize-snap`'s gaze and brow cells, `resolve-blink`'s hold). A
  block inherits them by naming the analysis, so they cannot be in one block's key
  and missing from another's.

  `:source` is in it and `:name` is NOT. A clip's name is a label a human types;
  two clips of the same footage under different names are the same analysis and
  must share it, which is the whole return on addressing.

  `:mode` is how the detector was RUN, and it belongs here for the same reason the
  version does. MediaPipe's video mode is a tracker and its image mode is not:
  over the same frames and the same model they disagree by up to 0.013 of frame
  width, which is a visible difference on a mouth. Optional, because the synthetic
  take has no running mode to declare and an absent field is how the other
  optional inputs already say \"not applicable\".

  `:range` is `[first end)` source frames when only part of the footage was
  analysed, and absent for all of it — so every analysis made before ranges
  existed keeps its address. It is in here because a partial analysis is a
  different artifact: without it, detecting frames 40–90 would be saved under the
  same key as the whole take, and the next conversion of the whole take would be
  handed fifty frames."
  [{:keys [detector version source footage frames fps aspect seed mode tracking range]}]
  (when-not (and (string? detector) (seq detector) (string? version) (seq version))
    (throw (ex-info "an analysis names its detector and the detector's VERSION: an upgrade that silently reuses old landmarks is the failure content addressing exists to prevent"
                    {:detector detector :version version})))
  (canon/write (cond-> {:scheme scheme
                        :detector detector
                        :version version
                        :frames frames
                        :fps fps
                        :aspect aspect}
                 source  (assoc :source source)
                 footage (assoc :footage footage)
                 seed    (assoc :seed seed)
                 mode    (assoc :mode mode)
                 tracking (assoc :tracking tracking)
                 range   (assoc :range range))))

(defn analysis
  "An analysis record with its `:id` filled in. The record is tier 1 — it says
  what produced the clip's channels — and the id is what `:generated :analysis`
  carries on every generated channel."
  [record]
  (assoc record :id (sha/key-of (analysis-descriptor record))))

;; ---------------------------------------------------------------------------
;; the observation masks

(defn- feature-name
  "An explicit subject or feature id as a descriptor string."
  [id]
  (subs (str id) 1))

(defn observation
  "A digest of exactly the absence data one block reads.

  Digested rather than inlined, for two reasons. A descriptor is meant to be READ
  — a stale bake presents as a picture that will not update, and the descriptor is
  the only thing that can say which input moved — and a 229-frame boolean mask
  inlined in it would bury the knobs it sits beside. And it is per-block: the eye
  block reads `:eye-r` and `:eye-l`'s presence and no other feature's, so a gap in
  one brow does not rewrite the mouth's address for nothing.

  Head tracks name their subject and follow its detection mask."
  [features {:keys [detected presence]}]
  (let [wanted (sort-by str (distinct features))
        masks  (into {} (map (fn [id] [id (mapv boolean (get presence id))]))
                     (filter #(contains? presence %) wanted))]
    (when (or detected (seq masks))
      (sha/key-of (canon/write {:scheme scheme
                                :detected (when detected (mapv boolean detected))
                                :presence (into {} (map (fn [[id m]] [(feature-name id) m]))
                                                (sort-by (comp str key) masks))})))))

;; ---------------------------------------------------------------------------
;; the blocks

(def block-knobs
  "Per block, the settings its BYTES depend on. The invalidation table, and the
  thing `address-test` refuses to take on trust.

  Two entries worth reading twice, because both are asymmetries a reasonable
  person would call a mistake:

  The EYE block does not depend on `blink-cut`. A blink is a `[:vis]` key on the
  eye's interior — tier 1, editable, a handful of transitions — and the lid
  geometry underneath it is the same either way. `iris-size` and `pupil-size` are
  missing for the same reason: both land on framed channels, not in a block.

  The TEETH block depends on `aperture-cut`, which nothing else in the table does.
  `condition/interior` will not smooth a contour on a frame the teeth are not
  shown on, and whether they are shown starts with the mouth being open — so the
  mouth's threshold reaches the pixel geometry, while the mouth's own vertex
  budget does not reach the teeth at all (the crop is taken from raw landmarks).
  It reads like a mistake in both directions and is neither."
  {"geom"       [:anchor-avg :contour-avg :verts]
   "source/dense" []
   "source/detected" []
   "source/crops" []
   "source/interior" [:blob-grow :cavity-erode :min-area :teeth-verts
                      :tongue-reject :top-bias]
   "head-pos"   [:anchor-avg]
   "head-rot"   [:anchor-avg]
   "head-scale" [:anchor-avg]
   ;; The head's fit itself, registering the footage under it — see
   ;; `freeze/plate-part`. The image height it divides by is the footage's, which
   ;; the analysis already names.
   "plate-pos"   [:anchor-avg]
   "plate-rot"   [:anchor-avg]
   "plate-scale" [:anchor-avg]
   "eyes"      [:anchor-avg :contour-avg :eye-verts :lash-weight]
   "iris-pos"   [:anchor-avg :contour-avg :gaze-gain :gaze-step]
   "brows"      [:anchor-avg :contour-avg :brow-verts :brow-gain :brow-step :brow-weight]
   ;; `brow-pos` and not `contour-avg`: the ring is smoothed and the RAISE is not.
   ;; `condition/brows` takes the end heights straight from measure, medians them
   ;; for a rest position and snaps them onto a grid, and never passes them
   ;; through `condition/contours`. Asserted, not assumed — the biconditional in
   ;; address-test is what found it here.
   "brow-pos"   [:anchor-avg :brow-gain :brow-step]
   "teeth"      [:anchor-avg :aperture-cut :blob-grow :cavity-erode :min-area
                 :teeth-on :teeth-smooth :teeth-verts :tongue-reject :top-bias]})

(def knob-roles
  "knob -> the roles whose bytes it moves. The inverse of `block-knobs`, DERIVED.

  This is what a parameter UI wants — \"what stops being valid if I drag this\" —
  and deriving it is the whole point: `block-knobs` is the table `address-test`
  asserts by biconditional, so an answer computed from it cannot drift from an
  answer that is checked, and an answer written down beside it could.

  A knob absent from this map invalidates NO BLOCK, and that is a real answer
  rather than a gap. `:blink-cut`, `:iris-size` and `:pupil-size` are all absent,
  because a blink is `[:vis]` keys and the two sizes are framed channels: tier 1,
  editable, and rewritten by a re-freeze without a byte of tier 2 moving."
  (reduce (fn [m [role knobs]]
            (reduce (fn [m knob] (update m knob (fnil conj #{}) role)) m knobs))
          {}
          block-knobs))

(defn invalidates
  "The roles one knob's bytes depend on, or an empty set."
  [knob]
  (get knob-roles knob #{}))

(def area-roles
  "Feature area -> the block roles `freeze/part` freezes for it.

  The other half of a question neither table answers alone. `block-knobs` says
  which knobs reach a ROLE's bytes; this says which roles a FEATURE owns; and what
  a regeneration actually asks is which knobs reach one feature."
  {:mouth ["geom"]
   :eye   ["eyes" "iris-pos"]
   :brow  ["brows" "brow-pos"]
   :teeth ["teeth"]})

(def framed-knobs
  "Feature area -> the knobs its TIER 1 channels read.

  What `block-knobs` cannot answer and deliberately does not: a framed radius and a
  keyed `[:vis]` hold no bytes, so no block key moves when they move — and a
  re-freeze still has to happen or the knob does nothing at all. This is the half
  that used to live nowhere, and a regeneration had to guess at with a per-knob
  special case. `regenerate-test` asserts the biconditional over the union, the
  same way `address-test` does for `block-knobs`, so it is checked and not believed.

  `:aperture-cut` is here AND in the teeth block: it gates `mouth-in`'s visibility
  in tier 1 and the interior contour's smoothing in tier 2. One knob, two features,
  two routes — which is exactly why this cannot be a per-area list of its own."
  {:mouth #{:aperture-cut}
   :eye   #{:blink-cut :iris-size :pupil-size}
   :brow  #{}
   :teeth #{}})

(defn area-knobs
  "Every knob one feature area's frozen output depends on, across both tiers."
  [area]
  (into (get framed-knobs area #{}) (mapcat block-knobs) (get area-roles area)))

(defn block-descriptor
  "The canonical text naming one dense block.

  `:tracks` is in it and so is `:layout`: two blocks over the same inputs that
  pack a different number of tracks, or the same tracks in another order, are
  different bytes at the same offsets, and a reader that trusted the key would
  hand the left eye's geometry to the right one."
  [{:keys [role analysis params features tracks layout observation]}]
  (let [knobs (or (get block-knobs role)
                  (throw (ex-info "no invalidation table for this block role: add it to block-knobs in the same commit as the block, or its key cannot change when its bytes do"
                                  {:role role :roles (sort (keys block-knobs))})))
        missing (remove #(contains? params %) knobs)]
    (when (seq missing)
      (throw (ex-info "a knob this block's bytes depend on was not passed to the freeze"
                      {:role role :missing (vec missing)})))
    (canon/write {:scheme scheme
                  :role role
                  :analysis analysis
                  :params (select-keys params knobs)
                  :features (mapv feature-name features)
                  :tracks (vec tracks)
                  :layout layout
                  :observation observation})))

(defn block
  "`{:key :descriptor}` for one dense block. The descriptor travels with the bytes
  — see `arthur.domain.leaf` and clips/views.py — because the server verifies
  `sha256(descriptor) == key` on upload rather than trusting a name it was handed."
  [spec]
  (let [text (block-descriptor spec)]
    {:key (sha/key-of text) :descriptor text}))
